import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Card from '../../components/base/Card';
import { featuredProducts } from '../../mocks/products';
import { useCart } from '../../hooks/useCart';
import { useState } from 'react';

export default function HomePage() {
  const { addToCart } = useCart();
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});

  const handleImageError = (productId: number) => {
    setImageErrors(prev => ({ ...prev, [productId]: true }));
  };

  const getPlaceholderImage = (category: string, seq: number) => {
    const placeholders: Record<string, string> = {
      'eBooks': `https://public.readdy.ai/ai/img_res/a61ca31c122cf2859e306a1d535249b2.jpg${seq}&orientation=landscape`,
      'Image Libraries': `https://readdy.ai/api/search-image?query=professional%20stock%20photography%20collection%20showcase%20display%2C%20multiple%20high%20quality%20images%20in%20grid%20layout%2C%20diverse%20photo%20library%2C%20modern%20clean%20presentation%20style%2C%20professional%20photography%20samples&width=400&height=300&seq=imagelib-home${seq}&orientation=landscape`,
      'Mini Videos': `https://readdy.ai/api/search-image?query=professional%20video%20template%20showcase%20with%20modern%20motion%20graphics%2C%20social%20media%20video%20content%20display%2C%20sleek%20video%20production%20mockup%2C%20clean%20presentation%20style%2C%20dynamic%20visual%20design&width=400&height=300&seq=video-home${seq}&orientation=landscape`
    };
    return placeholders[category] || placeholders['eBooks'];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-r from-blue-900 to-purple-900 text-white py-24 lg:py-32 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 58, 138, 0.8), rgba(88, 28, 135, 0.8)), url('https://readdy.ai/api/search-image?query=Digital%20content%20creation%20workspace%20with%20ebooks%2C%20video%20editing%2C%20image%20libraries%2C%20professional%20creative%20environment%2C%20modern%20technology%20setup%2C%20clean%20minimalist%20design%2C%20inspiring%20atmosphere%2C%20high-tech%20creative%20studio&width=1200&height=600&seq=hero1&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Premium Digital Products for Content Creators
            </h1>
            <p className="text-xl lg:text-2xl text-blue-100 mb-8 leading-relaxed">
              Discover our exclusive collection of professional eBooks, high-quality image libraries, and engaging mini advertising videos designed to elevate your content and grow your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/products">
                <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100">
                  <i className="ri-shopping-bag-line"></i>
                  Browse Products
                </Button>
              </Link>
              <Link to="/about">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-900">
                  <i className="ri-play-circle-line"></i>
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our Digital Product Categories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to create compelling content and grow your digital presence
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card hover className="text-center p-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-book-open-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Professional eBooks</h3>
              <p className="text-gray-600 mb-6">
                Comprehensive guides and resources covering digital marketing, business strategies, and industry insights from experts.
              </p>
              <Link to="/products">
                <Button variant="outline" size="sm">
                  <i className="ri-arrow-right-line"></i>
                  Explore eBooks
                </Button>
              </Link>
            </Card>
            
            <Card hover className="text-center p-8">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-image-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Image Libraries</h3>
              <p className="text-gray-600 mb-6">
                High-resolution stock photography collections for business, lifestyle, technology, and creative projects.
              </p>
              <Link to="/products">
                <Button variant="outline" size="sm">
                  <i className="ri-arrow-right-line"></i>
                  Browse Images
                </Button>
              </Link>
            </Card>
            
            <Card hover className="text-center p-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-video-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Mini Advertising Videos</h3>
              <p className="text-gray-600 mb-6">
                Professional video templates and content perfect for social media marketing and advertising campaigns.
              </p>
              <Link to="/products">
                <Button variant="outline" size="sm">
                  <i className="ri-arrow-right-line"></i>
                  View Videos
                </Button>
              </Link>
            </Card>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Featured Digital Products
            </h2>
            <p className="text-xl text-gray-600">
              Hand-picked premium content to boost your creative projects
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-product-shop>
            {featuredProducts.slice(0, 12).map((product) => (
              <Card key={product.id} hover className="overflow-hidden">
                <div className="relative w-full h-48 bg-gray-100">
                  <img 
                    src={imageErrors[product.id] ? getPlaceholderImage(product.category, product.id) : product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top"
                    onError={() => handleImageError(product.id)}
                    loading="lazy"
                  />
                  <div className="absolute top-4 right-4">
                    <span className="bg-red-500 text-white px-2 py-1 rounded-full text-sm font-semibold">
                      Save ${product.originalPrice - product.price}
                    </span>
                  </div>
                  <div className="absolute top-4 left-4">
                    <span className="bg-black/70 text-white px-2 py-1 rounded text-xs">
                      {product.category}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-1">
                      <i className="ri-star-fill text-yellow-400 text-sm"></i>
                      <span className="text-sm text-gray-600">{product.rating}</span>
                      <span className="text-sm text-gray-400">({product.reviews})</span>
                    </div>
                    <span className="text-sm text-gray-500">{product.downloadCount} downloads</span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{product.name}</h3>
                  <p className="text-gray-600 mb-4 line-clamp-2">{product.description}</p>
                  
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <span className="text-2xl font-bold text-gray-900">${product.price}</span>
                      <span className="text-lg text-gray-500 line-through">${product.originalPrice}</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => addToCart({
                        id: product.id,
                        name: product.name,
                        price: product.price,
                        image: product.image,
                        category: product.category
                      })}
                      className="whitespace-nowrap cursor-pointer font-medium transition-all duration-200 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl px-3 py-1.5 text-sm rounded-md flex-1"
                    >
                      <i className="ri-shopping-cart-line"></i>
                      Add to Cart
                    </button>
                    <Link to={`/product-detail/${product.id}`}>
                      <Button variant="outline" size="sm">
                        <i className="ri-eye-line"></i>
                        View
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/products">
              <Button size="lg">
                View All Products
                <i className="ri-arrow-right-line"></i>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Digital Product Authority
            </h2>
            <p className="text-xl text-gray-600">
              Your trusted source for premium digital content
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-download-cloud-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Instant Download</h3>
              <p className="text-gray-600">Get immediate access to your purchases with secure download links</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Commercial License</h3>
              <p className="text-gray-600">Use our products for personal and commercial projects without restrictions</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-customer-service-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Expert Support</h3>
              <p className="text-gray-600">Get help from our team of digital content experts whenever you need it</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-refresh-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Regular Updates</h3>
              <p className="text-gray-600">Receive free updates and new content additions to keep your library fresh</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Business?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Join thousands of satisfied customers who have revolutionized their operations with our digital products and AI solutions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                <i className="ri-phone-line"></i>
                Schedule Consultation
              </Button>
            </Link>
            <Link to="/products">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600">
                <i className="ri-download-line"></i>
                Browse Products
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
